<script>
/**
 * `advisory-opcoes-tabela` — spec-011-advisory-engagements-do-comite ([T060],
 * `contracts/ui-components.md` §3, `design/wireframes.md` T2 bloco "OPÇÕES").
 *
 * Tabela BURRA das `Option` avaliadas por `evaluate_scenarios` (backend,
 * `core/painel/cenarios.py`): 8 campos comparáveis por opção
 * (`nome`/`descricao`/`impactoEsperado`/`capitalBrl`/`pessoas`/`prazo`/
 * `risco`/`dependencias`) mais `viavel`/`motivoDaInviabilidade`. Zero fetch,
 * zero store — só desenha o que recebe (`BaseTable`) e emite `selecionar`
 * (usado pelo `advisory-decisao-form`, [T061]).
 *
 * ⚠️ Divergência medida (`spec.md:373`/`:398`/`:421`, wireframe T2): a
 * INVIÁVEL é a opção **A** ("3 cidades simultâneas" — reprova por capital
 * E contratações), não a B pedida na task original. **B** ("Campinas +
 * BH") é a ESCOLHIDA pela recomendação (★). Sigo wireframe/spec.md (regra
 * 14 do PROMPT-BASE — errata > texto).
 *
 * ⚠️ Gap de backend (declarado ao team-lead): `_traduzir_opcao`
 * (`nos_advisory_persist.py::_traduzir_opcao`, [T032]) NUNCA junta
 * `estado['cenarios']` (`viavel`/`motivo_da_inviabilidade`,
 * `evaluate_scenarios`) à linha persistida — só entra no PROMPT da LLM.
 * Este componente trata `viavel === undefined`/`null` como "sem
 * avaliação", NUNCA inviável por omissão; quando o backend ligar os dois,
 * o componente já lê o campo, sem mudança de contrato.
 *
 * D-2 (design it.2): "Opção" é rótulo POSICIONAL (A/B/C via
 * `rotuloPosicao(index)`, wireframe T2), nunca `item.nome` — o backend
 * manda o `Option.name` livre da LLM (frase inteira, sem enum), que
 * quebraria a coluna de 72px. `item.nome` virou o TÍTULO da linha, no
 * topo da coluna "Descrição".
 *
 * D-8 (design it.2): colunas de prosa longa (Descrição/Impacto esperado/
 * Pessoas/Prazo/Risco/Dependências — D-33 estendeu de 4 para 6) usam
 * `line-clamp: 4`; clique no texto (`@click.stop`, evita o duplo efeito
 * com `selecionar` da linha) alterna `expandido[item.nome]` e remove o
 * clamp só daquela linha.
 *
 * Os 4 estados (`ui-components.md` §3): `carregando` (mensagem) · `vazio`
 * OU `opcoes=[]` mesmo com `estado='sucesso'` (§4) · `erro` (sem botão de
 * retomar — evento do cabeçalho do engagement, não desta tabela) ·
 * `sucesso` (a tabela).
 *
 * T118 (achado R-1 do T117): `opcaoDecidida` (nova) marca a linha que o
 * GESTOR escolheu (`decisao.opcaoEscolhida`) com "Decisão do gestor" — só
 * quando DIFERE da recomendada (`opcaoRecomendada`, proposta da IA);
 * coincidindo, a estrela já basta (`itemClass` nunca soma as duas).
 *
 * Sem `?.`/`??` (Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { toMoney } from '@utils/Format'

const ESTADO_CARREGANDO = 'carregando'
const ESTADO_VAZIO = 'vazio'
const ESTADO_ERRO = 'erro'

const TEXTO_SEM_DADO = '—'
const TEXTO_CARREGANDO = 'Avaliando as opções...'
const TEXTO_VAZIO = 'Nenhuma opção registrada ainda.'
const TEXTO_ERRO = 'Não foi possível carregar as opções.'
const TEXTO_INVIAVEL = 'Inviável'
const TEXTO_DECISAO_GESTOR = 'Decisão do gestor'
const ICONE_INVIAVEL = 'close-circle'
const ICONE_RECOMENDADA = 'star'
const ICONE_DECISAO_GESTOR = 'user-tag'

// Os 8 campos comparáveis do `Option` (`ui-components.md` §3) — 'Opção' é
// rótulo POSICIONAL (A/B/C, D-2 acima); `item.nome` vira o título da linha
// na coluna "Descrição", não um 9º campo.
const ROTULOS_POSICAO = ['A', 'B', 'C', 'D', 'E', 'F']
const HEADERS = [
  { text: 'Opção', value: 'nome', sortable: false, width: '72px' },
  { text: 'Descrição', value: 'descricao', sortable: false },
  { text: 'Impacto esperado', value: 'impactoEsperado', sortable: false },
  { text: 'Capital', value: 'capitalBrl', sortable: false, align: 'end' },
  { text: 'Pessoas', value: 'pessoas', sortable: false },
  { text: 'Prazo', value: 'prazo', sortable: false },
  { text: 'Risco', value: 'risco', sortable: false },
  { text: 'Dependências', value: 'dependencias', sortable: false },
]

const CLASSE_LINHA_INVIAVEL = 'advisory-opcoes-tabela__linha--inviavel'
const CLASSE_LINHA_RECOMENDADA = 'advisory-opcoes-tabela__linha--recomendada'
const CLASSE_LINHA_DECIDIDA = 'advisory-opcoes-tabela__linha--decidida'

export default {
  name: 'IntelligenceV2AdvisoryOpcoesTabela',
  props: {
    /**
     * `[{ nome, descricao, impactoEsperado, capitalBrl, pessoas, prazo,
     * risco, dependencias, viavel, motivoDaInviabilidade }]`
     * (`ui-components.md` §3). `capitalBrl` chega como STRING decimal.
     */
    opcoes: {
      type: Array,
      required: true,
    },
    /** `nome` da opção que a recomendação escolheu — salta na tabela (★). */
    opcaoRecomendada: {
      type: String,
      default: null,
    },
    /** T118 — `nome` da opção do GESTOR (`decisao.opcaoEscolhida`); marca
     * "Decisão do gestor" quando difere de `opcaoRecomendada`. */
    opcaoDecidida: {
      type: String,
      default: null,
    },
    /** `carregando` | `vazio` | `erro` | `sucesso`. */
    estado: {
      type: String,
      default: 'sucesso',
    },
  },
  data() {
    return {
      // D-8: linhas com o clamp removido, por `nome` — local, não persiste.
      expandido: {},
    }
  },
  computed: {
    headers() {
      return HEADERS
    },
    // D-2: A/B/C por POSIÇÃO em `opcoes`, nunca por `item.nome`.
    rotulosPorNome() {
      const mapa = {}
      this.opcoes.forEach((item, index) => {
        if (item && item.nome) mapa[item.nome] = ROTULOS_POSICAO[index] || String(index + 1)
      })
      return mapa
    },
    carregando() {
      return this.estado === ESTADO_CARREGANDO
    },
    erro() {
      return this.estado === ESTADO_ERRO
    },
    // Cobre o estado explícito E `opcoes=[]` com `estado='sucesso'` (§4).
    vazio() {
      if (this.carregando || this.erro) return false
      return this.estado === ESTADO_VAZIO || this.opcoes.length === 0
    },
    textoCarregando() {
      return TEXTO_CARREGANDO
    },
    textoVazio() {
      return TEXTO_VAZIO
    },
    textoErro() {
      return TEXTO_ERRO
    },
    textoInviavel() {
      return TEXTO_INVIAVEL
    },
    iconeInviavel() {
      return ICONE_INVIAVEL
    },
    iconeRecomendada() {
      return ICONE_RECOMENDADA
    },
    textoDecisaoGestor() {
      return TEXTO_DECISAO_GESTOR
    },
    iconeDecisaoGestor() {
      return ICONE_DECISAO_GESTOR
    },
    // S-4 (review front it.3): decora cada opção UMA vez por render (nunca
    // função por render no `<template>`) — mesmo padrão de
    // `view-advisory-list.vue::decorarItem`. Métodos originais seguem
    // existindo (`.unit.js` os testa direto); `clampAtivo` depende de
    // `this.expandido`, então `linhas` reavalia sozinho a cada expansão.
    linhas() {
      return this.opcoes.map((item) => {
        if (!item) return item
        return {
          ...item,
          rotulo: this.rotuloDaOpcao(item),
          ehInviavelLinha: this.ehInviavel(item),
          ehRecomendadaLinha: this.ehRecomendada(item),
          ehDecididaLinha: this.ehDecidida(item),
          motivoTexto: this.motivoDaOpcao(item),
          descricaoTexto: this.textoOuTraco(item.descricao),
          impactoTexto: this.textoOuTraco(item.impactoEsperado),
          riscoTexto: this.textoOuTraco(item.risco),
          pessoasTexto: this.textoOuTraco(item.pessoas),
          prazoTexto: this.textoOuTraco(item.prazo),
          capitalTexto: this.capitalFormatado(item),
          dependenciasTexto: this.dependenciasFormatadas(item),
          clampAtivoLinha: this.clampAtivo(item),
          classeLinha: this.itemClass(item),
        }
      })
    },
  },
  methods: {
    // `viavel` undefined/null (gap de backend) nunca é inviável por omissão.
    ehInviavel(item) {
      return Boolean(item) && item.viavel === false
    },
    ehRecomendada(item) {
      return Boolean(item) && Boolean(this.opcaoRecomendada) && item.nome === this.opcaoRecomendada
    },
    // T118 — a opção que o GESTOR escolheu, independente de coincidir com
    // `opcaoRecomendada` ou não (`itemClass` decide a classe, abaixo).
    ehDecidida(item) {
      return Boolean(item) && Boolean(this.opcaoDecidida) && item.nome === this.opcaoDecidida
    },
    // D-2: rótulo curto (A/B/C) — nunca `item.nome` cru.
    rotuloDaOpcao(item) {
      return (item && this.rotulosPorNome[item.nome]) || ''
    },
    motivoDaOpcao(item) {
      return (item && item.motivoDaInviabilidade) || ''
    },
    // `capitalBrl` é STRING decimal (`ui-components.md` §6, nunca Number);
    // só guardamos null/undefined/'' para não desenhar "R$ null".
    capitalFormatado(item) {
      const valor = item && item.capitalBrl
      if (valor === null || valor === undefined || valor === '') return TEXTO_SEM_DADO
      return toMoney(valor)
    },
    dependenciasFormatadas(item) {
      const lista = (item && item.dependencias) || []
      return lista.length ? lista.join('; ') : TEXTO_SEM_DADO
    },
    textoOuTraco(valor) {
      return valor || TEXTO_SEM_DADO
    },
    itemClass(item) {
      const classes = []
      if (this.ehInviavel(item)) classes.push(CLASSE_LINHA_INVIAVEL)
      if (this.ehRecomendada(item)) classes.push(CLASSE_LINHA_RECOMENDADA)
      // T118 — "um selo só": a classe de decidida só entra quando a linha
      // NÃO É a recomendada (senão somaria as duas tintas).
      else if (this.ehDecidida(item)) classes.push(CLASSE_LINHA_DECIDIDA)
      return classes.join(' ')
    },
    onSelecionar(item) {
      if (!item || !item.nome) return
      this.$emit('selecionar', { nome: item.nome })
    },
    // D-8: clamp fica ATIVO até o gestor expandir aquela linha.
    clampAtivo(item) {
      return !(item && this.expandido[item.nome])
    },
    // `@click.stop` no chamador evita disparar `onSelecionar` (`@click:row`).
    alternarExpansao(item) {
      if (!item || !item.nome) return
      this.expandido = { ...this.expandido, [item.nome]: !this.expandido[item.nome] }
    },
    // D-19 (design it.3, T097): as 5 áreas expansíveis só respondiam ao
    // mouse (mesmo defeito do D-6 em `view-advisory-list.vue`). Enter/
    // Espaço expandem igual ao clique.
    onKeydownExpansao(event, item) {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      this.alternarExpansao(item)
    },
  },
}
</script>

<template>
  <div class="advisory-opcoes-tabela d-flex flex-column">
    <p
      v-if="carregando"
      class="az-text-secondary az-size-12 mb-0"
      data-test="advisory-opcoes-carregando"
    >
      {{ textoCarregando }}
    </p>

    <p
      v-else-if="erro"
      class="az-text-secondary az-size-12 mb-0 advisory-opcoes-tabela__erro"
      data-test="advisory-opcoes-erro"
    >
      {{ textoErro }}
    </p>

    <p
      v-else-if="vazio"
      class="az-text-secondary az-size-12 mb-0"
      data-test="advisory-opcoes-vazio"
    >
      {{ textoVazio }}
    </p>

    <BaseTable
      v-else
      bordered
      hide-default-footer
      :headers="headers"
      :items="linhas"
      :items-per-page="-1"
      item-key="nome"
      :no-data-text="textoVazio"
      data-test="advisory-opcoes-tabela"
      @click:row="onSelecionar"
    >
      <template v-slot:item.nome="{ item }">
        <div
          v-if="item"
          class="advisory-opcoes-tabela__celula advisory-opcoes-tabela__celula--opcao d-flex align-center az-gap-xsmall"
          :class="item.classeLinha"
        >
          <span class="az-text-primary az-size-14 font-medium">{{ item.rotulo }}</span>
          <icon-svg
            v-if="item.ehRecomendadaLinha"
            :name="iconeRecomendada"
            :size="14"
            color="warning"
            data-test="advisory-opcoes-icone-recomendada"
          />
        </div>
      </template>

      <template v-slot:item.descricao="{ item }">
        <div
          v-if="item"
          class="advisory-opcoes-tabela__celula d-flex flex-column az-gap-xsmall py-2"
          :class="item.classeLinha"
        >
          <span
            v-if="item.nome"
            class="az-text-primary az-size-12 font-medium advisory-opcoes-tabela__clique"
            role="button"
            tabindex="0"
            data-test="advisory-opcoes-titulo-linha"
            @click.stop="alternarExpansao(item)"
            @keydown.stop="onKeydownExpansao($event, item)"
          >
            {{ item.nome }}
          </span>
          <span
            class="az-text-secondary az-size-12 advisory-opcoes-tabela__clique"
            :class="{ 'advisory-opcoes-tabela__clamp': item.clampAtivoLinha }"
            role="button"
            tabindex="0"
            data-test="advisory-opcoes-descricao"
            @click.stop="alternarExpansao(item)"
            @keydown.stop="onKeydownExpansao($event, item)"
          >
            {{ item.descricaoTexto }}
          </span>
          <div
            v-if="item.ehInviavelLinha"
            class="d-flex align-center az-gap-xsmall advisory-opcoes-tabela__motivo"
            data-test="advisory-opcoes-motivo-inviabilidade"
          >
            <icon-svg :name="iconeInviavel" :size="14" color="var(--danger)" class="flex-shrink-0" />
            <BaseStatus text preserve-case :content="textoInviavel" color="danger" />
            <span class="az-text-secondary az-size-12">{{ item.motivoTexto }}</span>
          </div>
          <!-- T118: só quando a opção decidida DIFERE da recomendada — "um
               selo só" quando coincidem (a estrela na coluna Opção já basta). -->
          <div
            v-if="item.ehDecididaLinha && !item.ehRecomendadaLinha"
            class="d-flex align-center az-gap-xsmall"
            data-test="advisory-opcoes-selo-decisao-gestor"
          >
            <icon-svg :name="iconeDecisaoGestor" :size="14" color="var(--neutral-600)" class="flex-shrink-0" />
            <BaseStatus text preserve-case :content="textoDecisaoGestor" color="neutral-600" />
          </div>
        </div>
      </template>

      <template v-slot:item.impactoEsperado="{ item }">
        <div v-if="item" class="advisory-opcoes-tabela__celula" :class="item.classeLinha">
          <span
            class="advisory-opcoes-tabela__clique"
            :class="{ 'advisory-opcoes-tabela__clamp': item.clampAtivoLinha }"
            role="button"
            tabindex="0"
            @click.stop="alternarExpansao(item)"
            @keydown.stop="onKeydownExpansao($event, item)"
          >
            {{ item.impactoTexto }}
          </span>
        </div>
      </template>

      <template v-slot:item.capitalBrl="{ item }">
        <div
          v-if="item"
          class="advisory-opcoes-tabela__celula d-flex align-center justify-end"
          :class="item.classeLinha"
        >
          {{ item.capitalTexto }}
        </div>
      </template>

      <template v-slot:item.pessoas="{ item }">
        <div v-if="item" class="advisory-opcoes-tabela__celula" :class="item.classeLinha">
          <span
            class="advisory-opcoes-tabela__clique"
            :class="{ 'advisory-opcoes-tabela__clamp': item.clampAtivoLinha }"
            role="button"
            tabindex="0"
            @click.stop="alternarExpansao(item)"
            @keydown.stop="onKeydownExpansao($event, item)"
          >
            {{ item.pessoasTexto }}
          </span>
        </div>
      </template>

      <template v-slot:item.prazo="{ item }">
        <div v-if="item" class="advisory-opcoes-tabela__celula" :class="item.classeLinha">
          <span
            class="advisory-opcoes-tabela__clique"
            :class="{ 'advisory-opcoes-tabela__clamp': item.clampAtivoLinha }"
            role="button"
            tabindex="0"
            @click.stop="alternarExpansao(item)"
            @keydown.stop="onKeydownExpansao($event, item)"
          >
            {{ item.prazoTexto }}
          </span>
        </div>
      </template>

      <template v-slot:item.risco="{ item }">
        <div v-if="item" class="advisory-opcoes-tabela__celula" :class="item.classeLinha">
          <span
            class="advisory-opcoes-tabela__clique"
            :class="{ 'advisory-opcoes-tabela__clamp': item.clampAtivoLinha }"
            role="button"
            tabindex="0"
            @click.stop="alternarExpansao(item)"
            @keydown.stop="onKeydownExpansao($event, item)"
          >
            {{ item.riscoTexto }}
          </span>
        </div>
      </template>

      <template v-slot:item.dependencias="{ item }">
        <div v-if="item" class="advisory-opcoes-tabela__celula" :class="item.classeLinha">
          <span
            class="advisory-opcoes-tabela__clique"
            :class="{ 'advisory-opcoes-tabela__clamp': item.clampAtivoLinha }"
            role="button"
            tabindex="0"
            @click.stop="alternarExpansao(item)"
            @keydown.stop="onKeydownExpansao($event, item)"
          >
            {{ item.dependenciasTexto }}
          </span>
        </div>
      </template>
    </BaseTable>
  </div>
</template>

<style lang="sass" scoped>
.advisory-opcoes-tabela__erro
  color: var(--danger)

// M-1 (review front it.6) — Vuetify 2.0.11 NÃO TEM `item-class` do
// v-data-table (`grep -rn itemClass node_modules/vuetify/lib/` = zero); o
// contorno (`fee-lab-migracao-tabela.vue:183-190`) é aplicar o estilo por
// CÉLULA nos scoped slots (`:class="item.classeLinha"` acima), nunca no
// `<tr>`. `__celula` estica o wrapper pro padding de CADA `<td>` do
// Vuetify (`0 16px`) não cortar o tingido.
.advisory-opcoes-tabela__celula
  margin: 0 -16px
  padding: 0 16px
  box-sizing: border-box
  min-height: 100%

.advisory-opcoes-tabela__linha--inviavel
  background: color-mix(in srgb, var(--danger) 8%, white)

// "Estado selecionado salta" (design-telas-analiticas): borda na cor da
// marca + fundo tingido; a borda vai só na 1ª coluna ("Opção").
.advisory-opcoes-tabela__linha--recomendada
  background: color-mix(in srgb, var(--primary) 6%, white)

.advisory-opcoes-tabela__celula--opcao.advisory-opcoes-tabela__linha--recomendada
  border-left: 2px solid var(--primary)

// T118 — tinta neutra (nunca a cor da marca): linha que o gestor escolheu.
.advisory-opcoes-tabela__linha--decidida
  background: color-mix(in srgb, var(--neutral-600) 6%, white)

.advisory-opcoes-tabela__motivo
  line-height: 1.4

// D-8: prosa longa não estoura a altura da linha — clique expande.
.advisory-opcoes-tabela__clamp
  display: -webkit-box
  -webkit-line-clamp: 4
  -webkit-box-orient: vertical
  overflow: hidden

.advisory-opcoes-tabela__clique
  cursor: pointer
</style>
